import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Wallet, Key, Download, Upload, Copy, 
  Eye, EyeOff, Shield, AlertTriangle, CheckCircle2,
  Lock, Unlock, RefreshCw, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TreasuryManagementPage = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [privateKey, setPrivateKey] = useState('');
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  const [createForm, setCreateForm] = useState({
    network: 'polygon',
    wallet_name: ''
  });
  
  const [importForm, setImportForm] = useState({
    network: 'polygon',
    wallet_name: '',
    private_key: ''
  });

  const networks = [
    { id: 'polygon', name: 'Polygon', currency: 'POL' },
    { id: 'ethereum', name: 'Ethereum', currency: 'ETH' },
    { id: 'base', name: 'Base', currency: 'ETH' },
    { id: 'optimism', name: 'Optimism', currency: 'ETH' }
  ];

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/treasury/wallets`, {
        credentials: 'include'
      });
      const data = await response.json();
      setWallets(data.wallets || []);
    } catch (error) {
      toast.error('Failed to load treasury wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWallet = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/treasury/wallets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(createForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create wallet');
      }

      const data = await response.json();
      toast.success('Treasury wallet created successfully!');
      setShowCreateModal(false);
      setCreateForm({ network: 'polygon', wallet_name: '' });
      fetchWallets();
      
      // Show the newly created wallet
      setSelectedWallet(data.wallet);
      await generateQRCode(data.wallet.address);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleImportWallet = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/treasury/wallets/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(importForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to import wallet');
      }

      const data = await response.json();
      toast.success('Treasury wallet imported successfully!');
      setShowImportModal(false);
      setImportForm({ network: 'polygon', wallet_name: '', private_key: '' });
      fetchWallets();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleViewPrivateKey = async (wallet) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/treasury/wallets/${wallet.id}/private-key`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to retrieve private key');
      }

      const data = await response.json();
      setPrivateKey(data.private_key);
      setSelectedWallet(wallet);
      setShowKeyModal(true);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleExportKey = async () => {
    if (!exportPassword || exportPassword.length < 8) {
      toast.error('Export password must be at least 8 characters');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/admin/treasury/wallets/${selectedWallet.id}/export`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ export_password: exportPassword })
        }
      );

      if (!response.ok) throw new Error('Export failed');

      const data = await response.json();
      
      // Download as file
      const exportData = JSON.stringify(data.export, null, 2);
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `treasury_${selectedWallet.network}_${Date.now()}.json`;
      a.click();
      
      toast.success('Encrypted key exported successfully!');
      setShowExportModal(false);
      setExportPassword('');
    } catch (error) {
      toast.error('Failed to export key');
    }
  };

  const handleDeactivate = async (walletId) => {
    if (!window.confirm('Deactivate this treasury wallet?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/treasury/wallets/${walletId}/deactivate`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Deactivation failed');

      toast.success('Treasury wallet deactivated');
      fetchWallets();
    } catch (error) {
      toast.error('Failed to deactivate wallet');
    }
  };

  const handleReactivate = async (walletId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/treasury/wallets/${walletId}/reactivate`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Reactivation failed');

      toast.success('Treasury wallet reactivated');
      fetchWallets();
    } catch (error) {
      toast.error('Failed to reactivate wallet');
    }
  };

  const generateQRCode = async (address) => {
    try {
      const qr = await QRCode.toDataURL(address);
      setQrCodeUrl(qr);
    } catch (error) {
      console.error('QR generation failed:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Treasury Wallet Management</h1>
            <p className="text-muted-foreground">Secure custody & key management</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImportModal(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Import Wallet
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Wallet
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{wallets.length}</div>
              <p className="text-sm text-muted-foreground">Total Wallets</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {wallets.filter(w => w.status === 'active').length}
              </div>
              <p className="text-sm text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {new Set(wallets.map(w => w.network)).size}
              </div>
              <p className="text-sm text-muted-foreground">Networks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-600">
                {wallets.reduce((sum, w) => sum + (w.transaction_count || 0), 0)}
              </div>
              <p className="text-sm text-muted-foreground">Total Transactions</p>
            </CardContent>
          </Card>
        </div>

        {/* Wallet List */}
        <Card>
          <CardHeader>
            <CardTitle>Treasury Wallets</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading wallets...</div>
            ) : wallets.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No treasury wallets configured</p>
                <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                  Create First Treasury Wallet
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {wallets.map((wallet) => (
                  <div key={wallet.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Wallet className="h-5 w-5 text-blue-600" />
                          <span className="font-bold text-lg">{wallet.wallet_name}</span>
                          <Badge variant="outline">{wallet.network.toUpperCase()}</Badge>
                          <Badge className={getStatusColor(wallet.status)}>{wallet.status}</Badge>
                          {wallet.imported && (
                            <Badge variant="outline" className="bg-blue-50">Imported</Badge>
                          )}
                        </div>
                        
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <strong>Address:</strong>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {wallet.address}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyToClipboard(wallet.address, 'Address')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedWallet(wallet);
                                generateQRCode(wallet.address);
                              }}
                            >
                              Show QR
                            </Button>
                          </div>
                          <div><strong>Created:</strong> {new Date(wallet.created_at).toLocaleString()}</div>
                          <div><strong>Transactions:</strong> {wallet.transaction_count}</div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewPrivateKey(wallet)}
                          className="text-red-600"
                        >
                          <Key className="h-4 w-4 mr-1" />
                          View Key
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedWallet(wallet);
                            setShowExportModal(true);
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Export
                        </Button>
                        {wallet.status === 'active' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeactivate(wallet.id)}
                          >
                            <Lock className="h-4 w-4 mr-1" />
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReactivate(wallet.id)}
                          >
                            <Unlock className="h-4 w-4 mr-1" />
                            Reactivate
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Wallet Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle>Create Treasury Wallet</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateWallet} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Network *</Label>
                    <select
                      value={createForm.network}
                      onChange={(e) => setCreateForm({...createForm, network: e.target.value})}
                      className="w-full border rounded-md p-2"
                      required
                    >
                      {networks.map(net => (
                        <option key={net.id} value={net.id}>{net.name} ({net.currency})</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Wallet Name *</Label>
                    <Input
                      value={createForm.wallet_name}
                      onChange={(e) => setCreateForm({...createForm, wallet_name: e.target.value})}
                      placeholder="e.g., Polygon Main Treasury"
                      required
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <strong>Important:</strong> A new wallet will be generated. Save the private key securely after creation.
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Shield className="h-4 w-4 mr-2" />
                      Create Secure Wallet
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Import Wallet Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle>Import Treasury Wallet</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleImportWallet} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Network *</Label>
                    <select
                      value={importForm.network}
                      onChange={(e) => setImportForm({...importForm, network: e.target.value})}
                      className="w-full border rounded-md p-2"
                      required
                    >
                      {networks.map(net => (
                        <option key={net.id} value={net.id}>{net.name} ({net.currency})</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Wallet Name *</Label>
                    <Input
                      value={importForm.wallet_name}
                      onChange={(e) => setImportForm({...importForm, wallet_name: e.target.value})}
                      placeholder="e.g., Existing Polygon Treasury"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Private Key *</Label>
                    <Input
                      type="password"
                      value={importForm.private_key}
                      onChange={(e) => setImportForm({...importForm, private_key: e.target.value})}
                      placeholder="0x..."
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Will be encrypted and stored securely
                    </p>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowImportModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Upload className="h-4 w-4 mr-2" />
                      Import Wallet
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* View Private Key Modal */}
        {showKeyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-2xl w-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Private Key Access
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-semibold">⚠️ CRITICAL SECURITY WARNING</p>
                  <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Never share this private key with anyone</li>
                    <li>This access is audit logged</li>
                    <li>Anyone with this key controls the wallet</li>
                  </ul>
                </div>

                {selectedWallet && (
                  <div className="space-y-2">
                    <Label>Wallet Address</Label>
                    <div className="p-3 bg-muted rounded font-mono text-sm">
                      {selectedWallet.address}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Private Key</Label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowPrivateKey(!showPrivateKey)}
                    >
                      {showPrivateKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="p-3 bg-muted rounded font-mono text-sm break-all">
                    {showPrivateKey ? privateKey : '••••••••••••••••••••••••••••••••'}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => copyToClipboard(privateKey, 'Private key')}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Private Key
                  </Button>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowKeyModal(false);
                      setPrivateKey('');
                      setShowPrivateKey(false);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Export Key Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle>Export Encrypted Key</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Export private key encrypted with a custom password for secure backup.
                </p>

                <div className="space-y-2">
                  <Label>Export Password *</Label>
                  <Input
                    type="password"
                    value={exportPassword}
                    onChange={(e) => setExportPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    minLength={8}
                  />
                  <p className="text-xs text-muted-foreground">
                    You'll need this password to decrypt the key later
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowExportModal(false);
                      setExportPassword('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleExportKey}>
                    <Download className="h-4 w-4 mr-2" />
                    Export Encrypted Key
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* QR Code Display */}
        {selectedWallet && qrCodeUrl && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setQrCodeUrl('')}>
            <Card className="max-w-sm w-full">
              <CardHeader>
                <CardTitle>Wallet QR Code</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center">
                  <img src={qrCodeUrl} alt="Wallet QR Code" className="w-64 h-64" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-mono break-all">{selectedWallet.address}</p>
                  <p className="text-xs text-muted-foreground mt-1">{selectedWallet.network.toUpperCase()}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
